import React, { useState, useEffect } from 'react';
import {
  useLocalParticipant,
  useRemoteParticipants,
  useTracks,
  TrackToggle,
  AudioTrack,
  VideoTrack
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import './FriendCollaborationMobile.css';

/**
 * Mobile-First Friend Collaboration Interface
 * 2025 Design Standards: iOS FaceTime + Google Meet + Discord
 *
 * Design Principles:
 * - Clean, minimal chrome (video-first)
 * - Large touch targets (48px minimum)
 * - System fonts and colors
 * - Bottom-only controls (thumb zone)
 * - Smooth animations (60fps)
 */
const FriendCollaborationMobile = ({
  problemContext,
  friendName,
  handleManualExit,
  sessionStartTime
}) => {
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();

  // Video tracks - onlySubscribed: false to get ALL tracks (like desktop)
  const screenShareTracks = useTracks([
    { source: Track.Source.ScreenShare, onlySubscribed: false }
  ]);
  const videoTracks = useTracks([
    { source: Track.Source.Camera, onlySubscribed: false }
  ]);
  const audioTracks = useTracks([
    { source: Track.Source.Microphone, onlySubscribed: false }
  ]);

  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [pipPosition, setPipPosition] = useState({ bottom: true, right: true }); // Draggable PiP
  const [connectionQuality, setConnectionQuality] = useState('good'); // good, poor, bad
  const [permissionError, setPermissionError] = useState(null);
  const [hasAudioPermission, setHasAudioPermission] = useState(false);
  const [hasVideoPermission, setHasVideoPermission] = useState(false);

  // Auto-hide controls after 3 seconds of no interaction (like iOS FaceTime)
  useEffect(() => {
    const timeout = setTimeout(() => setControlsVisible(false), 3000);
    return () => clearTimeout(timeout);
  }, []); // Only run once on mount

  // Session timer (updates every minute)
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionDuration(Math.floor((Date.now() - (sessionStartTime || Date.now())) / 60000));
    }, 60000);
    return () => clearInterval(timer);
  }, [sessionStartTime]);

  // Check media permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        // Check audio permission
        const audioPermission = await navigator.permissions.query({ name: 'microphone' });
        setHasAudioPermission(audioPermission.state === 'granted');

        audioPermission.addEventListener('change', () => {
          setHasAudioPermission(audioPermission.state === 'granted');
        });

        // Check video permission
        const videoPermission = await navigator.permissions.query({ name: 'camera' });
        setHasVideoPermission(videoPermission.state === 'granted');

        videoPermission.addEventListener('change', () => {
          setHasVideoPermission(videoPermission.state === 'granted');
        });
      } catch (err) {
        // Permission API not supported, will check on toggle
      }
    };

    checkPermissions();
  }, []);

  // Monitor connection quality (LiveKit API)
  useEffect(() => {
    if (!localParticipant) return;

    const updateQuality = () => {
      const quality = localParticipant.connectionQuality;
      if (quality === 'poor') setConnectionQuality('poor');
      else if (quality === 'lost') setConnectionQuality('bad');
      else setConnectionQuality('good');
    };

    // Initial check
    updateQuality();

    // Listen for quality changes
    localParticipant.on('connectionQualityChanged', updateQuality);
    return () => {
      localParticipant.off('connectionQualityChanged', updateQuality);
    };
  }, [localParticipant]);

  // Find participants (skip Mira agent)
  const friendParticipant = remoteParticipants.find(p =>
    !p.identity.includes('mira-agent') && !p.identity.startsWith('agent-')
  );
  const miraParticipant = remoteParticipants.find(p =>
    p.identity.includes('mira-agent') || p.identity.startsWith('agent-')
  );

  // Get tracks
  const friendScreenShare = friendParticipant
    ? screenShareTracks.find(track => track.participant.identity === friendParticipant.identity)
    : null;
  const friendFaceVideo = friendParticipant
    ? videoTracks.find(track => track.participant.identity === friendParticipant.identity)
    : null;
  const myVideoTrack = localParticipant
    ? videoTracks.find(track => track.participant.identity === localParticipant.identity)
    : null;

  // Mode detection - 2025 intelligent layouts
  const hasScreenShare = !!friendScreenShare;
  const hasFaceVideo = !!friendFaceVideo;
  const hasMyVideo = !!myVideoTrack && localParticipant?.isCameraEnabled;

  // Speaking detection for visual feedback (iOS-style)
  const friendIsSpeaking = friendParticipant?.isSpeaking || false;
  const iAmSpeaking = localParticipant?.isSpeaking || false;

  // Haptic feedback
  const haptic = () => {
    if (navigator.vibrate) navigator.vibrate(10);
  };

  // Show controls on tap
  const handleTap = () => {
    setControlsVisible(true);
    setTimeout(() => setControlsVisible(false), 3000);
  };

  // Toggle PiP position (iOS-style tap to move corners)
  const handlePipTap = (e) => {
    e.stopPropagation();
    haptic();
    setPipPosition(prev => ({
      bottom: !prev.bottom,
      right: prev.right
    }));
  };

  // Request microphone permission and toggle
  const handleMicToggle = async (e) => {
    e.stopPropagation();
    haptic();

    // If no permission, request it first
    if (!hasAudioPermission) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop()); // Stop immediately
        setHasAudioPermission(true);
        setPermissionError(null);
      } catch (err) {
        console.error('Microphone permission denied:', err);
        setPermissionError('Please allow microphone access in your browser settings');
        setTimeout(() => setPermissionError(null), 5000);
        return;
      }
    }

    // Toggle is handled by TrackToggle component
    setIsMuted(!isMuted);
  };

  // Request camera permission and toggle
  const handleCameraToggle = async (e) => {
    e.stopPropagation();
    haptic();

    // If no permission, request it first
    if (!hasVideoPermission) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop()); // Stop immediately
        setHasVideoPermission(true);
        setPermissionError(null);
      } catch (err) {
        console.error('Camera permission denied:', err);
        setPermissionError('Please allow camera access in your browser settings');
        setTimeout(() => setPermissionError(null), 5000);
        return;
      }
    }

    // Toggle is handled by TrackToggle component
    setIsCameraOn(!isCameraOn);
  };

  return (
    <div className="mobile-call" onClick={handleTap}>
      {/* Main content area - Adaptive based on mode */}
      <div className="video-container">
        {/* MODE 1: Screen Share (Homework) - Priority display */}
        {hasScreenShare && (
          <div className="screen-share">
            <VideoTrack
              trackRef={friendScreenShare}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        )}

        {/* MODE 2: Face-to-face (No screen share) */}
        {!hasScreenShare && hasFaceVideo && (
          <div className={`face-large ${friendIsSpeaking ? 'speaking' : ''}`}>
            <VideoTrack
              trackRef={friendFaceVideo}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {friendIsSpeaking && <div className="speaking-indicator" />}
          </div>
        )}

        {/* MODE 3: Audio-only fallback */}
        {!hasScreenShare && !hasFaceVideo && (
          <div className="audio-only">
            <div className="avatar">
              {friendIsSpeaking && <div className="pulse" />}
              <div className="initial">{friendName?.[0] || '?'}</div>
            </div>
          </div>
        )}

        {/* Audio tracks - Always render */}
        {audioTracks
          .filter(trackRef => trackRef.participant !== localParticipant)
          .map((trackRef) => (
            <AudioTrack key={trackRef.participant.identity} trackRef={trackRef} />
          ))}
      </div>

      {/* Picture-in-Picture - Friend's face (when screen sharing) */}
      {hasScreenShare && hasFaceVideo && (
        <div className={`pip-small ${friendIsSpeaking ? 'speaking' : ''}`}>
          <VideoTrack
            trackRef={friendFaceVideo}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {friendIsSpeaking && <div className="speaking-ring" />}
        </div>
      )}

      {/* Picture-in-Picture - My video (adaptive sizing + tap to move) */}
      {hasMyVideo && (
        <div
          className={`${hasScreenShare ? 'pip-tiny' : 'pip-medium'} ${iAmSpeaking ? 'speaking' : ''} pip-interactive`}
          onClick={handlePipTap}
          style={{
            bottom: pipPosition.bottom ? undefined : 'auto',
            top: pipPosition.bottom ? undefined : '88px',
            right: pipPosition.right ? '16px' : undefined,
            left: pipPosition.right ? undefined : '16px',
          }}
        >
          <VideoTrack
            trackRef={myVideoTrack}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {iAmSpeaking && <div className="speaking-ring" />}

          {/* Subtle move indicator */}
          <div className="pip-move-hint">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      )}

      {/* Permission error toast - iOS style */}
      {permissionError && (
        <div className="permission-error">
          🔒 {permissionError}
        </div>
      )}

      {/* Connection quality indicator - Google Meet style */}
      {connectionQuality !== 'good' && (
        <div className={`connection-quality ${connectionQuality}`}>
          {connectionQuality === 'poor' ? '⚠️ Weak connection' : '❌ Connection issues'}
        </div>
      )}

      {/* Top bar - session info (fades when controls hidden) */}
      <div className={`top-bar ${controlsVisible ? 'visible' : 'hidden'}`}>
        <span className="duration">{sessionDuration} min</span>
        <span className="name">{friendName || 'Friend'}</span>
      </div>

      {/* Bottom controls - Google Meet style large buttons */}
      <div className={`bottom-controls ${controlsVisible ? 'visible' : 'hidden'}`}>
        <div className="control-grid">
          {/* Microphone - Primary (TrackToggle IS a button) */}
          <TrackToggle
            source={Track.Source.Microphone}
            showIcon={false}
            className={`control-btn primary ${isMuted ? 'off' : 'on'}`}
            onClick={handleMicToggle}
          >
            <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {isMuted ? (
                <>
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8" />
                  <line x1="2" y1="2" x2="22" y2="22" strokeLinecap="round" />
                </>
              ) : (
                <>
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8" />
                </>
              )}
            </svg>
            <span className="label">{isMuted ? 'Unmute' : 'Mute'}</span>
          </TrackToggle>

          {/* Camera (TrackToggle IS a button) */}
          <TrackToggle
            source={Track.Source.Camera}
            showIcon={false}
            className={`control-btn ${isCameraOn ? 'on' : 'off'}`}
            onClick={handleCameraToggle}
          >
            <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {isCameraOn ? (
                <>
                  <path d="M23 7l-7 5 7 5V7z" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </>
              ) : (
                <>
                  <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3" />
                  <line x1="2" y1="2" x2="22" y2="22" strokeLinecap="round" />
                </>
              )}
            </svg>
            <span className="label">Camera</span>
          </TrackToggle>

          {/* Mira (if available) */}
          {miraParticipant && (
            <button
              className="control-btn"
              onClick={(e) => {
                e.stopPropagation();
                haptic();
                // TODO: Open Mira help
              }}
            >
              <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <circle cx="12" cy="17" r="0.5" fill="currentColor" />
              </svg>
              <span className="label">Help</span>
            </button>
          )}

          {/* End call - Destructive */}
          <button
            className="control-btn end"
            onClick={(e) => {
              e.stopPropagation();
              haptic();
              if (window.confirm('Leave study session?')) {
                handleManualExit();
              }
            }}
          >
            <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22 3L2 3a2 2 0 0 0-2 2v4c0 1.1.9 2 2 2h2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-2 15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7h16v7z" transform="rotate(135 12 12)" />
            </svg>
            <span className="label">Leave</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FriendCollaborationMobile;
